/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  AdminListMessagesParams,
  AdminMessagePage,
  Batch,
  ConflictResponse,
  ForbiddenResponse,
  GetMessageStatsParams,
  InternalErrorResponse,
  ListMessagesParams,
  Message,
  MessagePage,
  MessageStats,
  NotFoundResponse,
  PayloadTooLargeResponse,
  PaymentRequiredResponse,
  SendMessages202,
  SendMessagesBody,
  TooManyRequestsResponse,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getSendMessagesUrl = () => {




  return `/messages`
}

/**
 * Accepted for delivery through the chosen device, or the default one.
 * The plan's limits apply (429 `limit_exceeded`), an unverified email
 * cannot send (403 `email_not_verified`), and past the limit an
 * overage pack may be bought from the wallet.
 * @summary Send one message to one or more recipients
 */
export const sendMessages = async (sendMessagesBody: SendMessagesBody, options?: Parameters<typeof apiFetch>[1]): Promise<SendMessages202> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return apiFetch<SendMessages202>(getSendMessagesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(sendMessagesBody)
  }
);}





export const getSendMessagesMutationKey = () => ['sendMessages'] as const;

export const getSendMessagesMutationOptions = <TError = UnauthorizedResponse | PaymentRequiredResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendMessages>>, TError,SendMessagesMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof sendMessages>>, TError,SendMessagesMutationVariables, TContext> => {

const mutationKey = getSendMessagesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof sendMessages>>, SendMessagesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  sendMessages(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SendMessagesMutationResult = NonNullable<Awaited<ReturnType<typeof sendMessages>>>
    export type SendMessagesMutationBody = SendMessagesBody
    export type SendMessagesMutationError = UnauthorizedResponse | PaymentRequiredResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse
    export type SendMessagesMutationVariables = {data: SendMessagesBody}

    /**
 * @summary Send one message to one or more recipients
 */
export const useSendMessages = <TError = UnauthorizedResponse | PaymentRequiredResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | PayloadTooLargeResponse | UnprocessableResponse | TooManyRequestsResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof sendMessages>>, TError,SendMessagesMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof sendMessages>>,
        TError,
        SendMessagesMutationVariables,
        TContext
      > => {
      return useMutation(getSendMessagesMutationOptions(options), queryClient);
    }
    export const getListMessagesUrl = (params?: ListMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["deviceIds"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/messages?${stringifiedParams}` : `/messages`
}

/**
 * A device or batch in the filters that is not the caller's answers 404 `device_not_listed` or `batch_not_listed`.
 * @summary The caller's message history
 */
export const listMessages = async (params?: ListMessagesParams, options?: Parameters<typeof apiFetch>[1]): Promise<MessagePage> => {

  return apiFetch<MessagePage>(getListMessagesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMessagesInfiniteQueryKey = (params?: ListMessagesParams,) => {
    return [
    'infinite', `/messages`, ...(params ? [params] : [])
    ] as const;
    }

export const getListMessagesQueryKey = (params?: ListMessagesParams,) => {
    return [
    `/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMessagesInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof listMessages>>, ListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData, QueryKey, ListMessagesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMessagesInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMessages>>, QueryKey, ListMessagesParams['cursor']> = ({ signal, pageParam }) => listMessages({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData, QueryKey, ListMessagesParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMessagesInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof listMessages>>>
export type ListMessagesInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listMessages>>, ListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListMessagesParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData, QueryKey, ListMessagesParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMessages>>,
          TError,
          Awaited<ReturnType<typeof listMessages>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listMessages>>, ListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData, QueryKey, ListMessagesParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMessages>>,
          TError,
          Awaited<ReturnType<typeof listMessages>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listMessages>>, ListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData, QueryKey, ListMessagesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's message history
 */

export function useListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof listMessages>>, ListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData, QueryKey, ListMessagesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMessagesInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getListMessagesQueryOptions = <TData = Awaited<ReturnType<typeof listMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: ListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMessagesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMessages>>> = ({ signal }) => listMessages(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof listMessages>>>
export type ListMessagesQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useListMessages<TData = Awaited<ReturnType<typeof listMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  ListMessagesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMessages>>,
          TError,
          Awaited<ReturnType<typeof listMessages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMessages<TData = Awaited<ReturnType<typeof listMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMessages>>,
          TError,
          Awaited<ReturnType<typeof listMessages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMessages<TData = Awaited<ReturnType<typeof listMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's message history
 */

export function useListMessages<TData = Awaited<ReturnType<typeof listMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: ListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMessages>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMessagesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGetMessageStatsUrl = (params?: GetMessageStatsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/messages/stats?${stringifiedParams}` : `/messages/stats`
}

/**
 * `to` defaults to now and `from` to 30 days before it; at most 366 days.
 * @summary Daily counts of sent, delivered, failed and received messages
 */
export const getMessageStats = async (params?: GetMessageStatsParams, options?: Parameters<typeof apiFetch>[1]): Promise<MessageStats> => {

  return apiFetch<MessageStats>(getGetMessageStatsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMessageStatsQueryKey = (params?: GetMessageStatsParams,) => {
    return [
    `/messages/stats`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetMessageStatsQueryOptions = <TData = Awaited<ReturnType<typeof getMessageStats>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: GetMessageStatsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageStats>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMessageStatsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMessageStats>>> = ({ signal }) => getMessageStats(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMessageStats>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMessageStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getMessageStats>>>
export type GetMessageStatsQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useGetMessageStats<TData = Awaited<ReturnType<typeof getMessageStats>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  GetMessageStatsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageStats>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMessageStats>>,
          TError,
          Awaited<ReturnType<typeof getMessageStats>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMessageStats<TData = Awaited<ReturnType<typeof getMessageStats>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: GetMessageStatsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageStats>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMessageStats>>,
          TError,
          Awaited<ReturnType<typeof getMessageStats>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMessageStats<TData = Awaited<ReturnType<typeof getMessageStats>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: GetMessageStatsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageStats>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Daily counts of sent, delivered, failed and received messages
 */

export function useGetMessageStats<TData = Awaited<ReturnType<typeof getMessageStats>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: GetMessageStatsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessageStats>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMessageStatsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGetMessageUrl = (id: string,) => {




  return `/messages/${id}`
}

/**
 * @summary One message
 */
export const getMessage = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Message> => {

  return apiFetch<Message>(getGetMessageUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMessageQueryKey = (id: string,) => {
    return [
    `/messages/${id}`
    ] as const;
    }


export const getGetMessageQueryOptions = <TData = Awaited<ReturnType<typeof getMessage>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessage>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMessageQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMessage>>> = ({ signal }) => getMessage(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMessage>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMessageQueryResult = NonNullable<Awaited<ReturnType<typeof getMessage>>>
export type GetMessageQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useGetMessage<TData = Awaited<ReturnType<typeof getMessage>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessage>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMessage>>,
          TError,
          Awaited<ReturnType<typeof getMessage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMessage<TData = Awaited<ReturnType<typeof getMessage>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessage>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMessage>>,
          TError,
          Awaited<ReturnType<typeof getMessage>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMessage<TData = Awaited<ReturnType<typeof getMessage>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessage>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One message
 */

export function useGetMessage<TData = Awaited<ReturnType<typeof getMessage>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMessage>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMessageQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getGetBatchUrl = (id: string,) => {




  return `/batches/${id}`
}

/**
 * Its messages are listed by `GET /messages?smsBatchId=`.
 * @summary One send's batch and its progress
 */
export const getBatch = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<Batch> => {

  return apiFetch<Batch>(getGetBatchUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBatchQueryKey = (id: string,) => {
    return [
    `/batches/${id}`
    ] as const;
    }


export const getGetBatchQueryOptions = <TData = Awaited<ReturnType<typeof getBatch>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatch>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBatchQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBatch>>> = ({ signal }) => getBatch(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBatch>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBatchQueryResult = NonNullable<Awaited<ReturnType<typeof getBatch>>>
export type GetBatchQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useGetBatch<TData = Awaited<ReturnType<typeof getBatch>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatch>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBatch>>,
          TError,
          Awaited<ReturnType<typeof getBatch>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBatch<TData = Awaited<ReturnType<typeof getBatch>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatch>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBatch>>,
          TError,
          Awaited<ReturnType<typeof getBatch>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBatch<TData = Awaited<ReturnType<typeof getBatch>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatch>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One send's batch and its progress
 */

export function useGetBatch<TData = Awaited<ReturnType<typeof getBatch>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBatch>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBatchQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListMessagesUrl = (params?: AdminListMessagesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["deviceIds"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? 'null' : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/messages?${stringifiedParams}` : `/admin/messages`
}

/**
 * @summary Every account's messages, for support
 */
export const adminListMessages = async (params?: AdminListMessagesParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminMessagePage> => {

  return apiFetch<AdminMessagePage>(getAdminListMessagesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListMessagesInfiniteQueryKey = (params?: AdminListMessagesParams,) => {
    return [
    'infinite', `/admin/messages`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListMessagesQueryKey = (params?: AdminListMessagesParams,) => {
    return [
    `/admin/messages`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListMessagesInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListMessages>>, AdminListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData, QueryKey, AdminListMessagesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListMessagesInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListMessages>>, QueryKey, AdminListMessagesParams['cursor']> = ({ signal, pageParam }) => adminListMessages({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData, QueryKey, AdminListMessagesParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListMessagesInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListMessages>>>
export type AdminListMessagesInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListMessages>>, AdminListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListMessagesParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData, QueryKey, AdminListMessagesParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListMessages>>,
          TError,
          Awaited<ReturnType<typeof adminListMessages>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListMessages>>, AdminListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData, QueryKey, AdminListMessagesParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListMessages>>,
          TError,
          Awaited<ReturnType<typeof adminListMessages>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListMessages>>, AdminListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData, QueryKey, AdminListMessagesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every account's messages, for support
 */

export function useAdminListMessagesInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListMessages>>, AdminListMessagesParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListMessagesParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData, QueryKey, AdminListMessagesParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListMessagesInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListMessagesQueryOptions = <TData = Awaited<ReturnType<typeof adminListMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListMessagesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListMessages>>> = ({ signal }) => adminListMessages(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof adminListMessages>>>
export type AdminListMessagesQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListMessages<TData = Awaited<ReturnType<typeof adminListMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListMessagesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListMessages>>,
          TError,
          Awaited<ReturnType<typeof adminListMessages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListMessages<TData = Awaited<ReturnType<typeof adminListMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListMessages>>,
          TError,
          Awaited<ReturnType<typeof adminListMessages>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListMessages<TData = Awaited<ReturnType<typeof adminListMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Every account's messages, for support
 */

export function useAdminListMessages<TData = Awaited<ReturnType<typeof adminListMessages>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListMessagesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListMessages>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListMessagesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






